import { assert } from '@ember/debug';
import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import Service, { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import type CookiesService from 'ember-cookies/services/cookies';
import config from 'garaje/config/environment';
import type DatadogService from 'garaje/services/datadog';
import type LocalStorageService from 'garaje/services/local-storage';
import type OnlineService from 'garaje/services/online';
import type StateService from 'garaje/services/state';
import extractRouteParams from 'garaje/utils/extract-route-params';
import { logLCPToLogRocket } from 'garaje/utils/log-rocket-helpers';
import pickBy from 'lodash/pickBy';
import LogRocket from 'logrocket';
import { tracked } from 'tracked-built-ins';
import { v4 as uuid } from 'uuid';

const PII_FIELDS = ['email', 'phone', 'name'];

interface EnvoyContext {
  user_id?: string;
  company_id?: string;
  location_id?: string;
  fe_name: string;
  fe_version: string;
  path: string;
}

export default class MetricsService extends Service {
  @service declare cookies: CookiesService;
  @service declare datadog: DatadogService;
  @service declare localStorage: LocalStorageService;
  @service declare online: OnlineService;
  @service declare state: StateService;
  @service declare router: RouterService;

  get notABoss(): boolean {
    return this.localStorage.getItem('is_boss') === 'false' || !this.localStorage.getItem('is_boss');
  }

  @tracked jobId: string | null = null; // used by trackJobEvent method, set by startJob method

  #pathName!: string;

  constructor(properties: object) {
    super(properties);

    this.#pathName = location.pathname;
    this.router.on('routeWillChange', (transition) => this.#routeWillChange(transition));
  }

  /**
   * @param userId A unique identifier for the current user.
   * @param traits Data about the current user to be sent to segment.
   * @param options Additional options passed to Segment - e.g. the Intercom user identity hash.
   *
   * @return window.analytics
   *
   * https://segment.com/docs/sources/website/analytics.js/#identify
   */
  identify(userId: string, traits: AnalyticsTraits = {}, options = {}): void {
    const compactedTraits = pickBy(traits, isPresent);

    if (window && window.analytics && this.notABoss) {
      void this.online.runWhenOnline(() => {
        window.analytics.identify(userId, compactedTraits, options);
        if (config.logRocketAppId) {
          const filteredTraits = pickBy(traits, (_value, key) => !PII_FIELDS.includes(key));
          filteredTraits['name'] = traits['name'] = userId;
          // @ts-expect-error - not compatible with logrocket types
          LogRocket.identify(userId, filteredTraits);
          logLCPToLogRocket();
        }
      });
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getStateEventProperties() {
    if (!this.state) {
      return null;
    }
    return {
      user: this.state.currentUser
        ? {
            id: this.state.currentUser.id,
          }
        : null,
      company: this.state.currentCompany
        ? {
            id: this.state.currentCompany.id,
            name: this.state.currentCompany.name,
          }
        : null,
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  pickStateContextForLogging() {
    if (!this.state) {
      return null;
    }
    return {
      user_id: this.state.currentUser?.id,
      company: this.state.currentCompany
        ? {
            id: this.state.currentCompany.id,
            name: this.state.currentCompany.name,
          }
        : null,
      location: this.state.currentLocation
        ? {
            id: this.state.currentLocation.id,
          }
        : null,
    };
  }

  /**
   *
   * @param event The name of the event you’re tracking.
   * @param properties A dictionary of properties for the event. If the event was 'delivery picked up', it might have properties like pickedUpBy.
   * @param options A dictionary of options for segment.
   *
   * @return window.analytics
   *
   * https://segment.com/docs/sources/website/analytics.js/#track
   */
  trackEvent(event: string, properties: AnalyticsTraits = {}, options: EventOptions = {}): void {
    const compactedProperties = pickBy(properties, isPresent);
    compactedProperties['project'] = 'dashboard';
    if (window && window.analytics && this.notABoss) {
      options = this.#setContext(options);
      void this.online.runWhenOnline(() => {
        window.analytics.track(`[dashboard] ${event}`, compactedProperties, options);
        // @ts-expect-error - not compatible with logrocket types
        if (config.logRocketAppId) LogRocket.track(`[dashboard] ${event}`, compactedProperties);
      });
    }
  }

  /**
   * Same API as trackEvent, but adds the current job_id to the properties
   */
  trackJobEvent(event: string, properties: AnalyticsTraits, options = {}): void {
    if (this.jobId === null) {
      assert(
        `metrics.trackJobEvent was called without first calling metrics.startJob. Please call metrics.startJob to generate a new job_id for subsequent job events.`,
      );
    }
    properties['job_id'] = this.jobId;
    properties['project'] = 'dashboard';
    this.trackEvent(event, properties, options);
  }

  /**
   * Called to start a new job chain.
   * Subsequent calls to trackJobEvent will use the uuid generated by this method.
   */
  startJob(): void {
    this.jobId = uuid();
  }

  /**
   *
   * @param name A unique page name.
   * @param properties A dictionary of properties of the page. Note: url, title, referrer and path are collected automatically!
   * @param options A dictionary of options for segment.
   *
   * @return window.analytics
   *
   * https://segment.com/docs/sources/website/analytics.js/#page
   */
  trackPage(name: string, properties: Record<string, unknown> = {}, options: EventOptions = {}): void {
    if (window && window.analytics && this.notABoss && isPresent(name)) {
      options = this.#setContext(options);
      properties['project'] = 'dashboard';
      void this.online.runWhenOnline(() => {
        window.analytics.page(`[dashboard] ${name}`, properties, options);
      });
    }
  }

  logMonitorError({
    event,
    debugExtras,
    error,
  }: {
    event: string;
    debugExtras?: Record<string, unknown>;
    error?: unknown;
  }): void {
    try {
      const stackTrace = (error instanceof Error ? error : new Error()).stack;
      this.datadog.logError(
        event,
        {
          ...this.pickStateContextForLogging(),
          error,
          debugExtras,
          stack_trace: stackTrace,
        },
        <Error>error,
      );
    } catch (e) {
      // eslint-disable-next-line
      console.error('error in logMonitorError', e, error);
    }
  }

  logMonitorEvent(event: string, properties = {}): void {
    try {
      this.datadog.logInfo(event, {
        ...this.pickStateContextForLogging(),
        extras: {
          ...properties,
        },
      });
    } catch (e) {
      // eslint-disable-next-line
      console.error('error in logMonitorEvent', e);
    }
  }

  getEnvoyContext(): EnvoyContext {
    const context: EnvoyContext = {
      fe_name: 'dashboard',
      fe_version: config.longRevision,
      path: this.#pathName,
    };

    const company_id = this.cookies.read('company_id');

    if (this.state.currentLocation?.id) context.location_id = this.state.currentLocation.id;
    if (this.state.currentUser?.id) context.user_id = this.state.currentUser?.id;
    if (company_id) context.company_id = company_id;

    return context;
  }

  #setContext(options: EventOptions): EventOptions {
    // See https://community.segment.com/t/x18vag for our universal traits (from identify call)
    options.context = options.context || {};
    if (typeof window.analytics.user === 'function') {
      options.context.traits = window.analytics.user().traits();
    } else {
      options.context.traits = {};
    }

    options.context.traits['user_id'] = this.state.currentUser?.id || null;
    return options;
  }

  #routeWillChange(transition: Transition): void {
    const nextRoute = transition.to;
    try {
      if (!nextRoute?.name?.includes('loading')) {
        const allParams = extractRouteParams(nextRoute);

        if (allParams.length > 0) {
          // Route has parameters - pass them to urlFor
          this.#pathName = this.router.urlFor(nextRoute.name, ...allParams);
        } else {
          // Route has no parameters. Do not pass additional parameters as they will break urlFor
          this.#pathName = this.router.urlFor(nextRoute.name);
        }
      }
    } catch (_error) {
      // no op catch any errors from urlFor taking in incorrect params that could blow up the app
    }
  }
}
